import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../config/axios';
import { 
  MdSearch, 
  MdClose, 
  MdRefresh,
  MdBusinessCenter,
  MdInfo,
  MdChevronRight,
  MdAssignment
} from 'react-icons/md';
import { FaFilePdf, FaFileExcel, FaFilter, FaCalendarAlt, FaUser, FaFileContract, FaBook, FaClock } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useLanguage } from '../context/LanguageContext';

const TimesheetUserReportPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    agent_id: '',
    start_date: '',
    end_date: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState([]);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Validation popup state
  const [showValidationPopup, setShowValidationPopup] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  // Agentleri getir
  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/agents', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Rapor oluştur
  const generateReport = async () => {
    // En azından bir filtre kriteri seçilmiş olmalı
    const hasFilter = filters.agent_id && filters.start_date && filters.end_date;

    if (!hasFilter) {
      setShowValidationPopup(true);
      return;
    }

    try {
      setIsLoading(true);
      
      // API'den timesheet verilerini getir
      const response = await api.get(`/timesheet/user-report`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: {
          agent_id: filters.agent_id,
          start_date: filters.start_date,
          end_date: filters.end_date
        }
      });
      
      setReportData(response.data || []);
      setReportGenerated(true);
      setCurrentPage(1); // Yeni rapor oluşturulduğunda ilk sayfaya dön
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(t('timesheetReport.generateError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('timesheetReport.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel
    const excelData = reportData.map((item, index) => ({
      [t('timesheetReport.serialNumber')]: index + 1,
      [t('common.agent')]: cleanTextForExcel(item.agent_name),
      [t('timesheetReport.companyName')]: cleanTextForExcel(item.customer_name),
      [t('timesheet.date')]: cleanTextForExcel(new Date(item.timesheet_date).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')),
      [t('timesheet.workHour')]: cleanTextForExcel(item.timesheet_workhour),
      [t('timesheet.detail')]: cleanTextForExcel(item.timesheet_detail)
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 8 },   // Sira No
      { wch: 25 },  // Agent
      { wch: 25 },  // Firma Adı
      { wch: 15 },  // Tarih
      { wch: 15 },  // Çalışma Saati
      { wch: 50 }   // Detay
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('timesheetReport.userReport'));

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('timesheetReport.userReport').replace(/\s+/g, '_')}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // PDF export  
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('timesheetReport.noDataToExport'));
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Helper function to handle Turkish characters
    const cleanText = (text) => {
      if (!text) return '';
      
      // Convert Turkish characters to Latin equivalents for PDF compatibility
      const turkishToLatin = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'I': 'I',
        'İ': 'I', 'i': 'i',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
      };
      
      let cleanedText = text.toString().replace(/undefined/g, '');
      
      // Replace Turkish characters
      for (const [turkish, latin] of Object.entries(turkishToLatin)) {
        cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
      }
      
      return cleanedText;
    };

    // Add title
    doc.setFontSize(16);
    doc.text(t('timesheetReport.userReport'), 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`${t('reports.reportDate')}: ${new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}`, 14, 30);

    // Prepare table data with clean text
    const tableData = reportData.map((item, index) => [
      index + 1,
      cleanText((item.agent_name || '').substring(0, 20) + (item.agent_name?.length > 20 ? '...' : '')),
      cleanText((item.customer_name || '').substring(0, 20) + (item.customer_name?.length > 20 ? '...' : '')),
      cleanText(new Date(item.timesheet_date).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')),
      cleanText(item.timesheet_workhour) || '0',
      cleanText((item.timesheet_detail || '').substring(0, 30) + (item.timesheet_detail?.length > 30 ? '...' : ''))
    ]);

    // Add table
    autoTable(doc, {
      head: [['#', t('common.agent'), t('timesheetReport.companyName'), t('timesheet.date'), t('timesheet.workHour'), t('timesheet.detail')]],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 7,
        cellPadding: 1,
        font: 'helvetica',
        fontStyle: 'normal'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        font: 'helvetica'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 12 },  // #
        1: { cellWidth: 35 },  // Agent
        2: { cellWidth: 40 },  // Firma Adi
        3: { cellWidth: 25 },  // Tarih
        4: { cellWidth: 25 },  // Calisma Saati
        5: { cellWidth: 60 }   // Detay
      },
      margin: { left: 10, right: 10 },
      tableWidth: '100%'
    });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('timesheetReport.userReport').replace(/\s+/g, '_')}_${currentDate}.pdf`;

    // Save file
    doc.save(filename);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      agent_id: '',
      start_date: '',
      end_date: ''
    });
    setReportData([]);
    setReportGenerated(false);
    setCurrentPage(1);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  return (
    <div className="h-full overflow-auto">
      {/* Breadcrumb */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex items-center text-sm">
          <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
            <span className="text-[#fff] font-medium">
              {t('timesheet.title')}
            </span>
            <MdChevronRight className="mx-2 text-[#fff]" size={20} />
            <span className="text-[#fff] font-medium">
              {t('timesheetReport.userReport')}
            </span>
          </div>
        </nav>
      </div>

      {/* Filter Panel */}
      <div className="dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
        {/* Filter Panel Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('timesheetReport.userReport')}</h1>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Agent Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('timesheetReport.agentSelection')}
              </label>
              <select
                value={filters.agent_id}
                onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('timesheetReport.selectAgent')}</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Başlangıç Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('common.startDate')}
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Bitiş Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('common.endDate')}
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
            {/* First Row - Show and Clear buttons */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={generateReport}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[#232b3b] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50"
              >
                <MdSearch size={16} />
                {isLoading ? t('reports.generatingReport') : t('common.show')}
              </button>

              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                <MdClose size={16} />
                {t('common.clear')}
              </button>
            </div>

            {/* Second Row - Export buttons */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={exportToPDF}
                disabled={!reportGenerated || reportData.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaFilePdf size={16} />
                {t('reports.exportToPDF')}
              </button>

              <button
                onClick={exportToExcel}
                disabled={!reportGenerated || reportData.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaFileExcel size={16} />
                {t('reports.exportToExcel')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.report')}</h1>
        </div>
        
        {reportGenerated ? (
          <>
            {/* Sayfa boyutu seçici */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('table.records')}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {t('table.totalRecordsFound', { count: reportData.length })}
                </span>
              </div>
            </div>

            {reportData.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full min-w-[600px] table-auto">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('common.agent')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('timesheetReport.companyName')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('timesheet.date')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('timesheet.workHour')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('timesheet.detail')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentData.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 ">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white rounded-bl-lg">
                            {item.agent_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {item.customer_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {new Date(item.timesheet_date).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.timesheet_workhour} {t('timesheet.hours')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate rounded-br-lg">
                            {item.timesheet_detail || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {currentData.map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-1 gap-3">
                        {/* Agent */}
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">{t('common.agent')}:</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.agent_name}</span>
                        </div>
                        
                        {/* Company */}
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">{t('timesheetReport.companyName')}:</span>
                          <span className="text-sm text-gray-900 dark:text-white truncate">{item.customer_name}</span>
                        </div>
                        
                        {/* Date and Work Hour */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-12 flex-shrink-0">{t('timesheet.date')}:</span>
                            <span className="text-sm text-gray-900 dark:text-white">
                              {new Date(item.timesheet_date).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-12 flex-shrink-0">{t('timesheet.workHour')}:</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.timesheet_workhour} {t('timesheet.hours')}
                            </span>
                          </div>
                        </div>
                        
                        {/* Detail */}
                        <div className="flex items-start">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 mt-0.5">{t('timesheet.detail')}:</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                            {item.timesheet_detail || '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sayfalama */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between mb-6">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {t('table.showing')}{' '}
                          <span className="font-medium">{indexOfFirstItem + 1}</span>
                          {' '}-{' '}
                          <span className="font-medium">
                            {Math.min(indexOfLastItem, reportData.length)}
                          </span>{' '}
                          / {t('table.total')}{' '}
                          <span className="font-medium">{reportData.length}</span>{' '}
                          {t('table.records')}
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                              currentPage === 1
                                ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {t('table.previous')}
                          </button>
                          {[...Array(totalPages)].map((_, index) => (
                            <button
                              key={index + 1}
                              onClick={() => handlePageChange(index + 1)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                                currentPage === index + 1
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {index + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                              currentPage === totalPages
                                ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {t('table.next')}
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <p>{t('reports.noDataFound')}</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            <p>{t('reports.useFiltersToGenerate')}</p>
          </div>
        )}
      </div>

      {/* Validation Popup */}
      {showValidationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="bg-red-500 text-white p-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                {t('timesheetReport.filterRequired')}
              </h3>
              <button 
                onClick={() => setShowValidationPopup(false)}
                className="text-white hover:text-red-200"
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('timesheetReport.agentFilterRequiredMessage')}
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowValidationPopup(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  {t('common.understood')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetUserReportPage;


